<template>
  <div id="app">
    <el-container class="layout">
      <!-- 顶栏：品牌 + 导航 + 用户 -->
      <el-header class="topbar" height="48px">
        <div class="topbar-left">
          <!-- Logo 品牌区 -->
          <div class="logo-area" @click="goHome">
            <span class="logo-title">数据平台</span>
          </div>

          <!-- 导航菜单 -->
          <el-menu
            :default-active="activeMenu"
            router
            mode="horizontal"
            text-color="#606266"
            active-text-color="#409EFF"
            class="topbar-menu"
          >
            <el-menu-item index="/data">
              <i class="el-icon-s-order"></i>
              <span slot="title">数据列表</span>
            </el-menu-item>
            <el-menu-item index="/annotation">
              <i class="el-icon-s-claim"></i>
              <span slot="title">场景标签</span>
            </el-menu-item>
            <el-menu-item index="/logs">
              <i class="el-icon-s-data"></i>
              <span slot="title">日志管理</span>
            </el-menu-item>
            <el-menu-item :index="platformEntry">
              <i class="el-icon-s-platform"></i>
              <span slot="title">模型训练</span>
            </el-menu-item>
          </el-menu>
        </div>

        <div class="topbar-right">
          <!-- 网络通道：内网加速 / 公网访问（选择记忆到 localStorage） -->
          <el-dropdown trigger="click" @command="handleNetworkCommand" class="network-select">
            <span class="network-trigger">
              <i class="el-icon-connection"></i>
              <span class="network-label">{{ networkLabel }}</span>
              <i class="el-icon-arrow-down"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="internal">内网加速</el-dropdown-item>
              <el-dropdown-item command="external">公网访问</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>

          <el-dropdown v-if="user" trigger="click" @command="handleUserCommand">
            <span class="user-trigger">
              <i class="el-icon-user-solid"></i>
              <span class="user-name">{{ username }}</span>
              <i class="el-icon-arrow-down"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="logout" icon="el-icon-switch-button">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </el-header>

      <!-- 主内容区：模型训练带左侧子导航，其余页面占满宽度 -->
      <el-container class="body">
        <el-aside v-if="inPlatformArea" class="sidebar" :width="sidebarCollapsed ? '64px' : '200px'">
          <el-menu
            :default-active="$route.path"
            :collapse="sidebarCollapsed"
            :collapse-transition="false"
            router
            class="sidebar-menu"
          >
            <el-menu-item v-for="item in platformMenu" :key="item.path" :index="item.path">
              <i :class="item.icon"></i>
              <span slot="title">{{ item.title }}</span>
            </el-menu-item>
          </el-menu>
          <div class="sidebar-toggle" @click="toggleSidebar">
            <i :class="sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
            <span v-if="!sidebarCollapsed">收起</span>
          </div>
        </el-aside>

        <el-main class="main">
          <router-view />
        </el-main>
      </el-container>
    </el-container>
  </div>
</template>

<script>
import { getSessionUser, logout } from './utils/auth';
import { getNetworkMode, setNetworkMode, NETWORK_LABELS, NETWORK_INTERNAL, NETWORK_EXTERNAL } from './utils/network';

/** 模型训练子导航，按数据流排序 */
const PLATFORM_MENU = [
  { path: '/blissrose/datasets', icon: 'el-icon-folder-opened', title: '数据集' },
  { path: '/blissrose/training', icon: 'el-icon-odometer', title: '训练监控' },
  { path: '/blissrose/models', icon: 'el-icon-box', title: '模型实例' },
  { path: '/blissrose/evaluation', icon: 'el-icon-data-analysis', title: '模型评估' },
  { path: '/blissrose/video', icon: 'el-icon-video-play', title: '视频推理' },
  { path: '/blissrose/gpu', icon: 'el-icon-cpu', title: 'GPU 监控' },
];

/** 顶栏「模型训练」进入的子页 */
const PLATFORM_ENTRY = PLATFORM_MENU[0].path;

/** 子导航折叠态，记忆到 localStorage */
const SIDEBAR_COLLAPSED_KEY = 'xbrainnet.algorithm_sidebar_collapsed';

export default {
  name: 'App',

  data() {
    return {
      // 挂载前 ensureSession 已就绪，直接读内存用户信息
      user: getSessionUser(),
      // 网络通道：localStorage 无记录时首访弹窗选择
      networkMode: getNetworkMode(),
      platformMenu: PLATFORM_MENU,
      platformEntry: PLATFORM_ENTRY,
      sidebarCollapsed: localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1',
    };
  },

  computed: {
    /** 顶栏高亮：区内任一子页都让顶栏「模型训练」保持选中 */
    activeMenu() {
      return this.inPlatformArea ? PLATFORM_ENTRY : this.$route.path;
    },
    /** 当前路由是否属于模型训练，决定左侧子导航出不出现 */
    inPlatformArea() {
      return PLATFORM_MENU.some(item => item.path === this.$route.path);
    },
    username() {
      return this.user ? this.user.username : '';
    },
    networkLabel() {
      return this.networkMode ? NETWORK_LABELS[this.networkMode] : '网络通道';
    },
  },

  mounted() {
    // 首访（localStorage 无记录）：弹一次网络通道选择
    if (!this.networkMode) {
      this.askNetworkMode();
    }
  },

  methods: {
    goHome() {
      if (this.$route.path !== '/') {
        this.$router.push('/');
      }
    },
    /** 折叠 / 展开左侧子导航 */
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, this.sidebarCollapsed ? '1' : '0');
    },
    /** 首访网络通道选择：两个大按钮强制二选一（禁用 X / 遮罩 / ESC） */
    askNetworkMode() {
      this.$confirm('', '请选择网络访问方式', {
        customClass: 'network-confirm',
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        confirmButtonText: '内网加速',
        cancelButtonText: '公网访问',
        distinguishCancelAndClose: true,
      }).then(() => {
        this.applyNetworkMode(NETWORK_INTERNAL);
      }).catch(action => {
        // 仅两个按钮可关弹窗；confirm → internal，cancel → external
        if (action === 'cancel') {
          this.applyNetworkMode(NETWORK_EXTERNAL);
        }
      });
    },
    /** 写入选择并同步本地状态 */
    applyNetworkMode(mode) {
      setNetworkMode(mode);
      this.networkMode = mode;
    },
    /** 顶栏下拉切换网络通道 */
    handleNetworkCommand(mode) {
      if (mode === this.networkMode) return;
      this.applyNetworkMode(mode);
      this.$message.success(`已切换至${NETWORK_LABELS[mode]}`);
    },
    handleUserCommand(command) {
      if (command === 'logout') {
        this.$confirm('确定要退出登录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }).then(() => {
          logout();
        }).catch(() => {});
      }
    },
  },
};
</script>

<style>
/* ========== 全局重置 ========== */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #app {
  height: 100%;
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ========== 根布局 ========== */
.layout {
  height: 100%;
  flex-direction: column;
  background-color: #f0f2f5;
}

/* ========== 顶栏 ========== */
.topbar {
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid #e8e8e8;
  z-index: 10;
}

.topbar-left {
  display: flex;
  align-items: center;
  height: 100%;
}

/* --- Logo 品牌区 --- */
.logo-area {
  display: flex;
  align-items: center;
  height: 100%;
  padding-right: 24px;
  margin-right: 16px;
  border-right: 1px solid #e8e8e8;
  cursor: pointer;
  user-select: none;
}

.logo-title {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  letter-spacing: 0.5px;
}

/* --- 横向导航菜单 --- */
.topbar-menu {
  border-bottom: none !important;
  background: transparent !important;
  height: 48px;
}

/* 横向菜单在 48px 条里不换行也不滚动 */
.topbar-menu .el-menu-item {
  height: 48px;
  line-height: 48px;
  margin: 0 2px;
  padding: 0 12px;
  font-size: 14px;
  user-select: none;
  border-bottom: 2px solid transparent;
}

.topbar-menu .el-menu-item i {
  margin-right: 6px;
  font-size: 15px;
}

.topbar-menu .el-menu-item:hover {
  background-color: #f0f2f5 !important;
}

.topbar-menu .el-menu-item.is-active {
  color: #409EFF !important;
  font-weight: 500;
  background-color: transparent !important;
  border-bottom-color: #409EFF !important;
}

/* --- 右侧网络通道 + 用户区 --- */
.topbar-right {
  display: flex;
  align-items: center;
}

.network-select {
  margin-right: 10px;
}

.network-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #606266;
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s;
  user-select: none;
}

.network-trigger:hover {
  background-color: #f0f2f5;
  color: #409EFF;
}

.network-trigger .el-icon-connection {
  margin-right: 5px;
  font-size: 15px;
}

.network-label {
  margin: 0 2px 0 2px;
}

.user-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #606266;
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s;
  user-select: none;
}

.user-trigger:hover {
  background-color: #f0f2f5;
  color: #409EFF;
}

.user-name {
  margin: 0 4px 0 6px;
}

/* ========== 主区：模型训练子导航 + 内容 ========== */
.body {
  flex: 1;
  min-height: 0;
}

.sidebar {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #e8e8e8;
}

.sidebar-menu {
  flex: 1;
  overflow-y: auto;
  border-right: none !important;
}

.sidebar-menu:not(.el-menu--collapse) .el-menu-item {
  height: 44px;
  line-height: 44px;
  padding-left: 22px !important;
  font-size: 14px;
  color: #606266;
}

.sidebar-menu .el-menu-item i {
  margin-right: 8px;
  font-size: 15px;
}

.sidebar-menu .el-menu-item:hover {
  background-color: #f0f2f5;
}

.sidebar-menu .el-menu-item.is-active {
  color: #409EFF !important;
  font-weight: 500;
  background-color: #ecf5ff;
  border-right: 3px solid #409EFF;
}

.sidebar-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  border-top: 1px solid #e8e8e8;
  color: #909399;
  font-size: 13px;
  cursor: pointer;
  user-select: none;
}

.sidebar-toggle:hover {
  background-color: #f0f2f5;
  color: #409EFF;
}

.sidebar-toggle i {
  font-size: 16px;
}

.sidebar-toggle span {
  margin-left: 6px;
}

/* --- 主内容区 --- */
.main {
  flex: 1;
  overflow-y: auto;
  background-color: #f0f2f5;
  padding: 20px 24px;
}

/* --- 滚动条美化 --- */
.main::-webkit-scrollbar {
  width: 6px;
}

.main::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 3px;
}

.main::-webkit-scrollbar-track {
  background: transparent;
}

/* --- 网络环境确认弹窗：标题居中、大按钮居中 --- */
.el-message-box.network-confirm {
  width: 400px;
  max-width: 90vw;
  padding-bottom: 14px; /* 覆盖默认 10px */
}
.el-message-box.network-confirm .el-message-box__header {
  padding: 14px 15px 10px;
}
.el-message-box.network-confirm .el-message-box__content {
  display: none; /* 无正文，隐藏空内容区 */
}
.el-message-box.network-confirm .el-message-box__title {
  display: block;
  text-align: center;
  font-size: 16px;
}
.el-message-box.network-confirm .el-message-box__btns {
  display: flex;
  justify-content: center;
  padding: 10px 15px 0;
}
.el-message-box.network-confirm .el-message-box__btns .el-button {
  width: 150px;
  height: 44px;
  margin: 0 10px;
  font-size: 15px;
  border-radius: 6px;
}
</style>
